import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, ArrowRight, MapPin, User, Leaf, ShoppingCart, Info, Gem, Award, Quote } from 'lucide-react';

const PomegranatesCatalogue = () => {
  const [selectedVariety, setSelectedVariety] = useState(null);

  const pomegranateVarieties = [
    {
      id: 'bhagwa',
      name: 'Bhagwa Pomegranate',
      subtitle: 'Ruby Red Premium Export',
      image: 'https://images.unsplash.com/photo-1732388538016-000e17086e48?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200',
      price: '₹120-180',
      unit: 'per kg',
      minOrder: '200 kg',
      season: 'September - February',
      origin: 'Solapur, Maharashtra',
      exportGrade: true,
      organic: false,
      grade: 'Export Grade A',
      arilColor: 'Ruby Red',
      weight: '300-500g per piece',
      description: 'Bhagwa is India\'s most popular pomegranate variety, known for its brilliant ruby-red arils, soft seeds, and excellent sweetness. The most exported Indian pomegranate variety with superior shelf life.',
      features: ['Ruby red arils', 'Soft seeds', 'High juice content', 'Long shelf life', 'Export quality', 'Rich antioxidants'],
      farmer: {
        name: 'Suresh Kumar Yadav',
        location: 'Solapur, Maharashtra',
        image: 'https://images.unsplash.com/photo-1608366911553-3296e6f5e2ef?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400',
        experience: '22 years',
        farmSize: '45 acres',
        story: 'When I started, pomegranate farming was a gamble - bacterial blight destroyed orchards overnight. SPANXPORT introduced us to disease-resistant varieties and connected us with agriculture universities. They also helped us get crop insurance. Today, my Bhagwa pomegranates reach supermarkets in Europe. The journey from despair to export-quality farmer - that\'s the real story.'
      },
      certifications: ['GlobalGAP', 'APEDA Certified', 'Phytosanitary Certified'],
      exportMarkets: ['UAE', 'UK', 'Netherlands', 'Germany', 'Saudi Arabia']
    },
    {
      id: 'arakta',
      name: 'Arakta Pomegranate',
      subtitle: 'Deep Red Indigenous Variety',
      image: 'https://images.unsplash.com/photo-1580636521086-7b0c742dd567?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200',
      price: '₹100-150',
      unit: 'per kg',
      minOrder: '200 kg',
      season: 'August - January',
      origin: 'Sangli, Maharashtra',
      exportGrade: true,
      organic: true,
      grade: 'Premium',
      arilColor: 'Deep Red',
      weight: '250-400g per piece',
      description: 'Arakta variety features deep red arils with a perfect balance of sweetness and slight tartness. Indigenous Indian variety with excellent nutritional profile and disease resistance.',
      features: ['Deep red color', 'Sweet-tart taste', 'Disease resistant', 'Indigenous variety', 'High antioxidants'],
      farmer: {
        name: 'Vishnu Maruti Patil',
        location: 'Sangli, Maharashtra',
        image: 'https://images.pexels.com/photos/29912210/pexels-photo-29912210.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '18 years',
        farmSize: '30 acres',
        story: 'Arakta is our heritage variety that thrives in dry conditions. When drought hit three consecutive years, many farmers abandoned their orchards. SPANXPORT arranged drip irrigation financing and organic certification support. Now our organic Arakta pomegranates command premium prices. More importantly, they gave us hope when we had none.'
      },
      certifications: ['Organic India', 'APEDA'],
      exportMarkets: ['UAE', 'Singapore', 'Malaysia']
    },
    {
      id: 'ganesh',
      name: 'Ganesh Pomegranate',
      subtitle: 'Pink Arils - Traditional Favorite',
      image: 'https://images.unsplash.com/photo-1608869857121-c0bcdf4fff2c?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200',
      price: '₹80-120',
      unit: 'per kg',
      minOrder: '300 kg',
      season: 'September - January',
      origin: 'Ahmednagar, Maharashtra',
      exportGrade: false,
      organic: false,
      grade: 'Grade A',
      arilColor: 'Light Pink',
      weight: '200-350g per piece',
      description: 'Ganesh variety is known for its light pink arils with soft seeds and mild sweet taste. Popular in domestic markets for its affordability and consistent quality.',
      features: ['Light pink arils', 'Very soft seeds', 'Mild sweetness', 'Affordable', 'Good for juice'],
      farmer: {
        name: 'Dattatraya Shinde',
        location: 'Ahmednagar, Maharashtra',
        image: 'https://images.unsplash.com/photo-1709532918611-77db9df9b27d?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400',
        experience: '25 years',
        farmSize: '35 acres',
        story: 'Ganesh pomegranates are named after Lord Ganesha - they have been part of religious offerings for generations. But selling was always a struggle - middlemen controlled the market. SPANXPORT opened direct channels to juice manufacturers who value our consistent quality. My grandfather\'s dream of dignified farming is finally coming true.'
      },
      certifications: ['FSSAI', 'APEDA'],
      exportMarkets: []
    },
    {
      id: 'mridula',
      name: 'Mridula Pomegranate',
      subtitle: 'Soft-Seeded Delicacy',
      image: 'https://images.pexels.com/photos/10654219/pexels-photo-10654219.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹90-140',
      unit: 'per kg',
      minOrder: '250 kg',
      season: 'October - February',
      origin: 'Pune, Maharashtra',
      exportGrade: true,
      organic: true,
      grade: 'Premium Export',
      arilColor: 'Dark Pink',
      weight: '280-450g per piece',
      description: 'Mridula is known for exceptionally soft seeds that are almost unnoticeable when eating. Perfect for fresh consumption with excellent sweetness and juiciness.',
      features: ['Extremely soft seeds', 'High sweetness', 'Juicy arils', 'Table variety', 'Premium quality'],
      farmer: {
        name: 'Anand Kulkarni',
        location: 'Pune, Maharashtra',
        image: 'https://images.pexels.com/photos/3339871/pexels-photo-3339871.jpeg?auto=compress&cs=tinysrgb&w=400',
        experience: '15 years',
        farmSize: '20 acres',
        story: 'Mridula variety was developed for consumers who love pomegranates but dislike hard seeds. Getting organic certification was expensive and complicated - SPANXPORT guided us through every step, even paying for initial audits. Now our Mridula pomegranates are featured in premium fruit baskets in Dubai. That pride is worth more than money.'
      },
      certifications: ['Organic India', 'GlobalGAP'],
      exportMarkets: ['UAE', 'UK', 'USA']
    },
    {
      id: 'super-bhagwa',
      name: 'Super Bhagwa',
      subtitle: 'Premium Export Selection',
      image: 'https://images.pexels.com/photos/7033653/pexels-photo-7033653.jpeg?auto=compress&cs=tinysrgb&w=1200',
      price: '₹150-220',
      unit: 'per kg',
      minOrder: '150 kg',
      season: 'October - January',
      origin: 'Nashik, Maharashtra',
      exportGrade: true,
      organic: false,
      grade: 'Super Premium',
      arilColor: 'Intense Ruby',
      weight: '400-600g per piece',
      description: 'Super Bhagwa is an enhanced selection of the Bhagwa variety, featuring larger fruit size, more intense ruby color, and higher sugar content. The premium choice for export markets.',
      features: ['Extra large size', 'Intense ruby color', 'Highest sweetness', 'Premium export', 'Gift quality'],
      farmer: {
        name: 'Raghunath Deshmukh',
        location: 'Nashik, Maharashtra',
        image: 'https://images.unsplash.com/photo-1709207513502-d9242031ab1b?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400',
        experience: '20 years',
        farmSize: '50 acres',
        story: 'Super Bhagwa requires meticulous care - each fruit is individually managed for size and color. The investment is high, and one bad season can wipe us out. SPANXPORT\'s advance payment contracts removed that risk. They also trained my team on international grading standards. Now my pomegranates are sold in Harrods, London. From a village farmer to international exporter - that\'s the SPANXPORT difference.'
      },
      certifications: ['GlobalGAP', 'APEDA', 'BRC Certified'],
      exportMarkets: ['UK', 'Netherlands', 'Germany', 'UAE', 'Saudi Arabia']
    }
  ];

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/65256/pomegranate-open-cores-fruit-65256.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Pomegranate orchards"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#263776]/95 via-[#263776]/70 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <Link to="/products/orivana-fresh" className="inline-flex items-center text-white/80 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to ORIVANA Fresh
          </Link>
          <Badge className="bg-[#F47D20] text-white mb-4 block w-fit">
            <Gem className="w-3 h-3 mr-1 inline" /> Ruby Red Goodness
          </Badge>
          <h1 className="text-5xl font-bold text-white mb-4">Pomegranates</h1>
          <p className="text-xl text-white/90 max-w-2xl">
            Premium Indian pomegranates - Bhagwa, Arakta, Ganesh varieties. Export-grade ruby red arils from Maharashtra's finest orchards.
          </p>
        </div>
      </section>

      {/* Export Banner */}
      <section className="bg-[#F47D20] py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-8 text-white text-sm flex-wrap">
            <span className="flex items-center"><Award className="w-4 h-4 mr-2" /> India's #1 Pomegranate Exporter</span>
            <span>|</span>
            <span>GlobalGAP & APEDA Certified</span>
            <span>|</span>
            <span>Rich in Antioxidants</span>
          </div>
        </div>
      </section>

      {/* Varieties Grid */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left: Varieties List */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-bold text-[#263776] mb-6">Select Variety</h2>
              <div className="space-y-4">
                {pomegranateVarieties.map((variety) => (
                  <Card 
                    key={variety.id}
                    className={`cursor-pointer transition-all duration-300 ${
                      selectedVariety?.id === variety.id 
                        ? 'ring-2 ring-[#F47D20] shadow-lg' 
                        : 'hover:shadow-md'
                    }`}
                    onClick={() => setSelectedVariety(variety)}
                    data-testid={`pomegranate-variety-${variety.id}`}
                  >
                    <CardContent className="p-4 flex items-center gap-4">
                      <img 
                        src={variety.image} 
                        alt={variety.name}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-[#263776]">{variety.name}</h3>
                          {variety.exportGrade && (
                            <Badge className="bg-red-100 text-red-700 text-xs">Export</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">{variety.origin}</p>
                        <p className="text-[#F47D20] font-bold mt-1">{variety.price} <span className="text-xs text-slate-400">{variety.unit}</span></p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-400" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Right: Selected Variety Details */}
            <div className="lg:col-span-2">
              {selectedVariety ? (
                <div className="sticky top-4">
                  <div className="relative rounded-xl overflow-hidden h-[350px] mb-6">
                    <img 
                      src={selectedVariety.image} 
                      alt={selectedVariety.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
                      <Badge className="bg-[#263776] text-white">{selectedVariety.grade}</Badge>
                      {selectedVariety.exportGrade && (
                        <Badge className="bg-red-600 text-white">
                          <Award className="w-3 h-3 mr-1" /> Export Grade
                        </Badge>
                      )}
                      {selectedVariety.organic && (
                        <Badge className="bg-green-600 text-white">
                          <Leaf className="w-3 h-3 mr-1" /> Organic
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h2 className="text-3xl font-bold text-[#263776] mb-2">{selectedVariety.name}</h2>
                      <p className="text-lg text-[#F47D20] font-medium mb-4">{selectedVariety.subtitle}</p>
                      <p className="text-slate-600 mb-4">{selectedVariety.description}</p>
                      
                      <div className="bg-slate-50 rounded-lg p-4 mb-4">
                        <h4 className="font-bold text-[#263776] mb-3 flex items-center">
                          <Info className="w-4 h-4 mr-2" /> Specifications
                        </h4>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div><span className="text-slate-500">Season:</span> <span className="font-medium">{selectedVariety.season}</span></div>
                          <div><span className="text-slate-500">Origin:</span> <span className="font-medium">{selectedVariety.origin}</span></div>
                          <div><span className="text-slate-500">Aril Color:</span> <span className="font-medium">{selectedVariety.arilColor}</span></div>
                          <div><span className="text-slate-500">Weight:</span> <span className="font-medium">{selectedVariety.weight}</span></div>
                          <div><span className="text-slate-500">Min Order:</span> <span className="font-medium">{selectedVariety.minOrder}</span></div>
                          <div><span className="text-slate-500">Grade:</span> <span className="font-medium">{selectedVariety.grade}</span></div>
                        </div>
                      </div>

                      {selectedVariety.exportMarkets.length > 0 && (
                        <div className="mb-4">
                          <h4 className="font-bold text-[#263776] mb-2">Export Markets</h4>
                          <div className="flex flex-wrap gap-2">
                            {selectedVariety.exportMarkets.map((market, idx) => (
                              <Badge key={idx} className="bg-red-100 text-red-700">
                                {market}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="mb-4">
                        <h4 className="font-bold text-[#263776] mb-2">Key Features</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedVariety.features.map((feature, idx) => (
                            <Badge key={idx} variant="outline" className="border-[#263776]/30">
                              {feature}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="font-bold text-[#263776] mb-2">Certifications</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedVariety.certifications.map((cert, idx) => (
                            <Badge key={idx} className="bg-[#263776]/10 text-[#263776]">
                              {cert}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div>
                      <Card className="mb-6 border-2 border-[#F47D20]/20">
                        <CardContent className="p-6">
                          <div className="text-3xl font-bold text-[#F47D20] mb-1">{selectedVariety.price}</div>
                          <div className="text-slate-500 mb-4">{selectedVariety.unit} (Wholesale/Export)</div>
                          <div className="text-sm text-slate-600 mb-4">
                            Minimum Order: <span className="font-bold">{selectedVariety.minOrder}</span>
                          </div>
                          <Button asChild className="w-full bg-[#F47D20] hover:bg-[#d66b1a] mb-3">
                            <Link to={`/products/orivana-fresh/enquiry?product=${selectedVariety.id}&category=pomegranates`}>
                              <ShoppingCart className="w-4 h-4 mr-2" />
                              Request Quote
                            </Link>
                          </Button>
                          <Button asChild variant="outline" className="w-full border-[#263776] text-[#263776]">
                            <Link to="/contact">
                              Contact Sales Team
                            </Link>
                          </Button>
                        </CardContent>
                      </Card>

                      <Card className="bg-gradient-to-br from-[#263776]/5 to-[#F47D20]/5">
                        <CardContent className="p-6">
                          <h4 className="font-bold text-[#263776] mb-4 flex items-center">
                            <User className="w-4 h-4 mr-2" /> Meet the Farmer
                          </h4>
                          <div className="flex items-start gap-4">
                            <img 
                              src={selectedVariety.farmer.image}
                              alt={selectedVariety.farmer.name}
                              className="w-20 h-20 rounded-full object-cover"
                            />
                            <div>
                              <h5 className="font-bold text-[#263776]">{selectedVariety.farmer.name}</h5>
                              <div className="flex items-center text-sm text-slate-500 mb-2">
                                <MapPin className="w-3 h-3 mr-1" />
                                {selectedVariety.farmer.location}
                              </div>
                              <div className="text-xs text-slate-400 mb-2">
                                {selectedVariety.farmer.experience} experience • {selectedVariety.farmer.farmSize}
                              </div>
                            </div>
                          </div>
                          <p className="text-sm text-slate-600 mt-4 italic">
                            "{selectedVariety.farmer.story}"
                          </p>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-[500px] bg-slate-50 rounded-xl">
                  <div className="text-center">
                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Gem className="w-8 h-8 text-red-600" />
                    </div>
                    <h3 className="text-xl font-bold text-[#263776] mb-2">Select a Variety</h3>
                    <p className="text-slate-500">Click on a pomegranate variety to view details</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Health Benefits Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="bg-red-100 text-red-700 mb-4">HEALTH BENEFITS</Badge>
            <h2 className="text-3xl font-bold text-[#263776] mb-4">Why Pomegranates?</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Pomegranates are packed with antioxidants, vitamins, and minerals that offer numerous health benefits
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { title: 'Rich Antioxidants', desc: '3x more than green tea' },
              { title: 'Heart Health', desc: 'Improves blood flow' },
              { title: 'Anti-inflammatory', desc: 'Reduces joint pain' },
              { title: 'Immune Boost', desc: 'High in Vitamin C' }
            ].map((benefit, idx) => (
              <Card key={idx} className="text-center p-6">
                <h4 className="font-bold text-[#263776] mb-2">{benefit.title}</h4>
                <p className="text-sm text-slate-500">{benefit.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-[#263776]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Looking for Bulk Pomegranate Supply?</h2>
          <p className="text-white/80 mb-6">We supply to exporters, juice manufacturers, and retail chains across India</p>
          <Button asChild size="lg" className="bg-[#F47D20] hover:bg-[#d66b1a]">
            <Link to="/products/orivana-fresh/enquiry">Get Export Quote</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default PomegranatesCatalogue;
